import * as Yup from 'yup';
import i18n from '../../i18n';

const constants = {
    titleEn: 'titleEn',
    titleSv: 'titleSv',
    descriptionEn: 'descriptionEn',
    descriptionSv: 'descriptionSv',
    externalWebsiteUrl: 'externalWebsiteUrl',
    venueName: 'venueName',
    address: 'address',
    zip: 'zip',
    city: 'city',
    image: 'image',
    selectedImage: 'selectedImage',
    file: 'file',
    photoUploadAllowed: 'photoUploadAllowed',
    photoCredit: 'photoCredit',
    startDate: 'startDate',
    endDate: 'endDate',
    date: 'date',
    startTime: 'startTime',
    endTime: 'endTime',
    location: 'location',
    categories: 'categories',
    dateAdmin: 'dateAdmin',
    submittedByEmail: 'submittedByEmail',
    consent: 'consent',
    mainCategory: 'mainCategory',
    subcategory: 'subcategory',
    isDigital: 'isDigital',
    isFree: 'isFree',
};

const {
    titleEn,
    titleSv,
    descriptionEn,
    descriptionSv,
    externalWebsiteUrl,
    venueName,
    closestStation,
    address,
    zip,
    city,
    image,
    selectedImage,
    file,
    photoUploadAllowed,
    photoCredit,
    startDate,
    endDate,
    date,
    startTime,
    endTime,
    location,
    categories,
    dateAdmin,
    submittedByEmail,
    consent,
    isFree,
    isDigital,
    mainCategory,
    subcategory,
} = constants;

export const startValues = {
    step1: {
        [titleEn]: '',
        [descriptionEn]: '',
        [titleSv]: '',
        [descriptionSv]: '',
    },
    step2: {
        [categories]: [],
        [mainCategory]: '',
        [subcategory]: '',
        [isFree]: false,
    },
    step3: {
        [isDigital]: false,
        [address]: '',
        [zip]: '',
        [city]: '',
        [venueName]: '',
        [closestStation]: '',
    },
    step4: {
        [startDate]: '',
        [endDate]: '',
        [date]: '',
        [startTime]: '',
        [endTime]: '',
        [dateAdmin]: [],
    },
    step5: {
        [image]: '',
        [selectedImage]: '',
        [file]: {},
        [photoUploadAllowed]: '',
        [photoCredit]: '',
    },
    step6: {
        [submittedByEmail]: '',
        [externalWebsiteUrl]: '',
        [consent]: '',
    },
    step7: {
        // something: '',
        // file: {},
    },
};

export const ErrorMessageSchemas = {
    step1: Yup.object().shape({
        [constants.titleEn]: Yup.string().required(
            i18n.t('EventSubmissionForm.validation.titleEnRequired')
        ),
        [constants.descriptionEn]: Yup.string()
            .min(
                100,
                i18n.t('EventSubmissionForm.validation.minLength', {
                    length: 100,
                })
            )
            .max(
                500,
                i18n.t('EventSubmissionForm.validation.maxLength', {
                    length: 500,
                })
            )
            .required(
                i18n.t('EventSubmissionForm.validation.descriptionEnRequired')
            ),
        [constants.titleSv]: Yup.string().required(
            i18n.t('EventSubmissionForm.validation.titleSvRequired')
        ),
        [constants.descriptionSv]: Yup.string()
            .min(
                100,
                i18n.t('EventSubmissionForm.validation.minLength', {
                    length: 100,
                })
            )
            .max(
                500,
                i18n.t('EventSubmissionForm.validation.maxLength', {
                    length: 500,
                })
            )
            .required(
                i18n.t('EventSubmissionForm.validation.descriptionSvRequired')
            ),
    }),
    step2: Yup.object().shape({
        [categories]: Yup.array().test({
            name: 'categories_test',
            exclusive: true,
            test: (value) => {
                if (value.length === 0) {
                    throw new Yup.ValidationError(
                        i18n.t(
                            'EventSubmissionForm.validation.atLeastOneCategory'
                        ),
                        null,
                        categories
                    );
                } else if (value.length > 3) {
                    throw new Yup.ValidationError(
                        i18n.t(
                            'EventSubmissionForm.validation.maxThreeCategories'
                        ),
                        null,
                        categories
                    );
                } else {
                    return true;
                }
            },
        }),
    }),
    step3: Yup.object().shape({
        [isDigital]: Yup.boolean(),
        [address]: Yup.string().when(isDigital, {
            is: false,
            then: Yup.string().required(
                i18n.t('EventSubmissionForm.validation.addressRequired')
            ),
            otherwise: Yup.string().notRequired(),
        }),
        [city]: Yup.string().when(isDigital, {
            is: false,
            then: Yup.string().required(
                i18n.t('EventSubmissionForm.validation.cityRequired')
            ),
            otherwise: Yup.string().notRequired(),
        }),
        [zip]: Yup.string().when(isDigital, {
            is: false,
            then: Yup.string().required(
                i18n.t('EventSubmissionForm.validation.zipRequired')
            ),
            otherwise: Yup.string().notRequired(),
        }),
    }),
    step4: Yup.object().shape({
        [dateAdmin]: Yup.array()
            .min(
                1,
                i18n.t('EventSubmissionForm.validation.atLeastOneOccurrence')
            )
            .required(
                i18n.t('EventSubmissionForm.validation.atLeastOneOccurrence')
            ),
    }),
    step5: Yup.object().shape({
        [image]: Yup.string().required(),
        [selectedImage]: Yup.string(),
        [photoUploadAllowed]: Yup.array().when(['selectedImage'], {
            is: (x) => !!x,
            then: Yup.array()
                .test({
                    name: 'photoUploadAllowed_test',
                    exclusive: true,
                    message: i18n.t(
                        'EventSubmissionForm.validation.youMustAgreeUpload'
                    ),
                    test: (value = []) => value.length > 0,
                })
                .required(),
            else: Yup.array(),
        }),
    }),
    step6: Yup.object().shape({
        [submittedByEmail]: Yup.string()
            .email(i18n.t('EventSubmissionForm.validation.email'))
            .required(
                i18n.t(
                    'EventSubmissionForm.validation.submittedByEmailRequired'
                )
            ),
        [consent]: Yup.array().test({
            name: 'consent_test',
            exclusive: true,
            message: i18n.t('EventSubmissionForm.validation.youMustAgree'),
            test: (value = []) => value.length > 0,
        }),
        [externalWebsiteUrl]: Yup.string().url(
            i18n.t('EventSubmissionForm.validation.url')
        ),
    }),
};
